import { Component, Element, Host, Prop, h } from '@stencil/core';

import { onOfferResubmit, onUpdateOfferInfo } from '../../../actions/actions.offer';
import vehicleValidateActions from '../../../actions/actions.validation.vehicle';
import { actions } from '../../../actions/actions.vehicle';
import { AutoCheckState } from '../../../store/store.autocheck';
import { OfferState } from '../../../store/store.offer';
import { state as validationState } from '../../../store/store.validation';
import { getMileageError, parseMileage } from '../../../utils/utils.vehicle';
@Component({
  tag: 'offer-simplified-resubmit',
  styleUrl: 'offer-simplified-resubmit.css',
})
export class OfferSimplifiedResubmit {
  @Element() el: HTMLElement;
  @Prop() autoCheck: AutoCheckState;
  @Prop() customer = { email: '', firstName: '', lastName: '', phone: '', zip: '' };
  @Prop() offer: OfferState;
  @Prop() hasMileageError: boolean = false;
  @Prop() errorMessage: string;
  @Prop() errors = validationState.form.vehicle;
  @Prop() vehicle = {
    drivetrain: '',
    engine: '',
    fullName: '',
    mileage: 0,
    transmission: '',
    trim: '',
    vin: '',
  };

  componentWillLoad() {
    if (this.vehicle.mileage) {
      this.validateMileage();
    }
  }

  updateOfferInfoClick() {
    if (this.hasMileageError) {
      return;
    }
    onUpdateOfferInfo();
  }

  offerResubmitClicked() {
    if (this.hasMileageError) {
      return;
    }
    onOfferResubmit();
  }

  validateMileage() {
    this.hasMileageError = !vehicleValidateActions.onValidateMileage();
    this.errorMessage = getMileageError(this.errors, this.offer.minimumMileageIneligibleThreshold);
  }

  onMileageChanged(e) {
    actions.updateMileage(e.detail);
    this.validateMileage();
  }

  onInputBlur() {
    this.validateMileage();
    if (this.hasMileageError) {
      return;
    }
  }

  render() {
    const { autoCheckFullReportLink, hasAccidents, hasPriorRentals, hasSingleOwner, reportDate, historyIcons } = this.autoCheck;
    const { accidentQuestionModifiedByDealer, canBeResubmitted, enableMileageIneligibleEditing, isKelleyBuysYourCar } = this.offer;
    const hasVehicleHistory = autoCheckFullReportLink.length > 1;
    const showSubmitButton = canBeResubmitted || enableMileageIneligibleEditing;
    const showKelleyBuysYourCar = isKelleyBuysYourCar;

    return (
      <Host>
        <certificate-layout class="@container" showKelleyBuysYourCar={showKelleyBuysYourCar} hideLinks={showKelleyBuysYourCar}>
          {showKelleyBuysYourCar ? (
            <div class="flex flex-col gap-y-6 mt-8">
              <h2 class="text-4xl text-clrs-black font-extrabold">Your Offer Has Expired</h2>
              <ico-button
                label="See What Your Car is Worth"
                buttonSize="large"
                class="block max-w-[295px]"
                whenClicked={() => (window.location.href = 'https://www.kbb.com/whats-my-car-worth?LNX=ICOKBYCELIGIBLE')}
              />
            </div>
          ) : (
            <div>
              <div class="font-open-sans mb-10">
                {enableMileageIneligibleEditing ? (
                  <div>
                    <h2 class="text-4xl font-extrabold">Update Mileage</h2>
                    <p class="font-semibold">Please enter the current mileage below.</p>
                  </div>
                ) : (
                  <div>
                    <h2 class="text-4xl font-extrabold">Resubmit Your Offer</h2>
                    <p class="font-semibold">Update your mileage and if anything changed with you vehicle please make the adjustment below.</p>
                  </div>
                )}
              </div>
              <div class="shadow-panel p-8 rounded-md mb-6">
                <h3 class="text-2xl mdLrg:mb-4 font-extrabold font-montserrat">Your Vehicle</h3>
                <p class="font-semibold font-open-sans">{this.vehicle.fullName}</p>
                <div class="font-semibold text-sm font-open-sans">
                  <p>Style: {this.vehicle.trim}</p>
                  <p>Engine: {this.vehicle.engine}</p>
                  <p>Transmission: {this.vehicle.transmission}</p>
                  <p>Drivetrain: {this.vehicle.drivetrain}</p>
                </div>
                <div class="mileage">
                  <input-text-box
                    class="w-full"
                    id="mileage"
                    onValueChange={this.onMileageChanged.bind(this)}
                    onBlur={this.onInputBlur.bind(this)}
                    label="Mileage"
                    maxLength={6}
                    hasGrayPlaceHolder
                    value={this.vehicle.mileage ? this.vehicle.mileage.toString() : ''}
                    parse={parseMileage}
                    isDisabled={!canBeResubmitted && !enableMileageIneligibleEditing}
                  />
                  {this.hasMileageError && showSubmitButton && (
                    <div>
                      <span class="flex text-xs leading-4 text-red-500 mt-[2px]">
                        <icon-alert class="mr-2 w-4 h-4 fill-red-500" />
                        {this.errorMessage}
                      </span>
                    </div>
                  )}
                </div>
                {!this.hasMileageError && showSubmitButton && (
                  <div>
                    {canBeResubmitted && (
                      <ico-hyperlink noUnderline={true} class="flex text-center text-sm" onClick={this.updateOfferInfoClick}>
                        Update items about your vehicle like Condition, Options, and more.
                      </ico-hyperlink>
                    )}
                    <div class="btn-resubmit">
                      <ico-button
                        buttonStyle="primary"
                        label={enableMileageIneligibleEditing ? 'Update' : 'Resubmit Offer'}
                        buttonSize="large"
                        whenClicked={() => this.offerResubmitClicked()}
                      />
                    </div>
                  </div>
                )}
              </div>
              {hasVehicleHistory && (
                <div>
                  <vehicle-history-report
                    autoCheckFullReportLink={autoCheckFullReportLink}
                    hasDealerEditedQuestion={accidentQuestionModifiedByDealer}
                    hasAccidents={hasAccidents}
                    hasPriorRentals={hasPriorRentals}
                    hasSingleOwner={hasSingleOwner}
                    reportGeneratedOnDate={reportDate}
                    vin={this.vehicle.vin}
                    slot="main"
                    historyIcons={historyIcons}
                  />
                </div>
              )}
              <offer-certificate-disclaimer />
            </div>
          )}
        </certificate-layout>
      </Host>
    );
  }
}
